import { Logo } from '@/components/Logo/Logo';
import {
  rotuloDaSituacao,
  situacoesEmOrdem,
  tokenDaSituacao,
  tokenDoPrazo,
} from '@/features/acoes/rotulos';
import type { SaudePrazo } from '@/types/api';

import estilos from './PaginaDeTokens.module.css';

/**
 * Showcase dos design tokens. Aceite da T04.
 *
 * Serve para conferir a olho, nos dois temas, o que a verificacao automatica de
 * contraste ja garante em numero: as 10 situacoes distinguiveis, os 4 estados de
 * prazo e a paleta oficial do guia.
 */

/*
  Rotulos e tokens vem de features/acoes/rotulos, e nao de uma copia local. A
  copia que existia aqui tinha os valores errados de SituacaoAcao
  ('AGUARDANDO_ANALISE' em vez de 'AguardandoAnalise') e ninguem percebeu, porque
  nada comparava esta lista com a resposta real da API.
*/
const situacoes = situacoesEmOrdem.map((chave) => ({
  chave,
  rotulo: rotuloDaSituacao[chave],
  token: tokenDaSituacao[chave],
}));

/* Exemplos de texto factual, como o painel escreve: numero em vez de adjetivo. */
const prazos: { chave: SaudePrazo; rotulo: string; token: string }[] = [
  { chave: 'SemPrazo', rotulo: 'Sem prazo', token: tokenDoPrazo.SemPrazo },
  { chave: 'NoPrazo', rotulo: 'No prazo', token: tokenDoPrazo.NoPrazo },
  {
    chave: 'ProximoVencimento',
    rotulo: 'Vence em 5 dias',
    token: tokenDoPrazo.ProximoVencimento,
  },
  { chave: 'Atrasado', rotulo: 'Atrasada 12 dias', token: tokenDoPrazo.Atrasado },
];

const paletaOficial = [
  { nome: 'Azul', hex: '#5469ed', token: '--fap-azul' },
  { nome: 'Azul Escuro', hex: '#321d6f', token: '--fap-azul-escuro' },
  { nome: 'Laranja', hex: '#ff7949', token: '--fap-laranja' },
  { nome: 'Verde', hex: '#afd629', token: '--fap-verde' },
  { nome: 'Cinza', hex: '#dee3ea', token: '--fap-cinza' },
];

export function PaginaDeTokens() {
  return (
    <main className={estilos.pagina}>
      <header className={estilos.cabecalho}>
        <h1 className={estilos.titulo}>Design tokens</h1>
        <p className={estilos.subtitulo}>
          Paleta e estados conforme o Guia de Identidade Visual e Verbal e as Decisoes 01, 02 e 13.
          Troque o tema no cabecalho para conferir os dois conjuntos.
        </p>
      </header>

      <section className={estilos.secao}>
        <h2 className={estilos.secaoTitulo}>Marca</h2>
        <p className={estilos.aviso}>
          Asset provisorio extraido do PDF do guia. Nao e oficial (Decisao 03). O build de producao
          falha enquanto ele estiver em uso.
        </p>
        <div className={estilos.marcas}>
          <div className={estilos.amostraDeMarca}>
            <Logo variante="positiva" altura={44} />
            <code className={estilos.token}>positiva</code>
          </div>
          <div className={`${estilos.amostraDeMarca} ${estilos.fundoEscuro}`}>
            <Logo variante="negativa" altura={44} />
            <code className={estilos.token}>negativa</code>
          </div>
          <div className={estilos.amostraDeMarca}>
            <Logo variante="simbolo" altura={44} />
            <code className={estilos.token}>simbolo</code>
          </div>
        </div>
      </section>

      <section className={estilos.secao}>
        <h2 className={estilos.secaoTitulo}>Paleta oficial</h2>
        <div className={estilos.paleta}>
          {paletaOficial.map((cor) => (
            <div key={cor.token} className={estilos.corItem}>
              <span className={estilos.corAmostra} style={{ background: `var(${cor.token})` }} />
              <span className={estilos.corNome}>{cor.nome}</span>
              <code className={estilos.token}>{cor.hex}</code>
            </div>
          ))}
        </div>
      </section>

      <section className={estilos.secao}>
        <h2 className={estilos.secaoTitulo}>Situacoes da acao</h2>
        <p className={estilos.subtitulo}>
          As 10 situacoes da maquina de estados. Cada uma combina simbolo, rotulo e cor: o estado
          nunca depende apenas da cor.
        </p>
        <div className={estilos.selos}>
          {situacoes.map((situacao) => (
            <span
              key={situacao.chave}
              className={estilos.selo}
              style={{ color: `var(${situacao.token})` }}
            >
              <span aria-hidden="true">&#9679;</span>
              {situacao.rotulo}
            </span>
          ))}
        </div>
      </section>

      <section className={estilos.secao}>
        <h2 className={estilos.secaoTitulo}>Saude do prazo</h2>
        <p className={estilos.subtitulo}>
          Derivada, nunca persistida. O rotulo e factual e quantificado, sem tom alarmista.
        </p>
        <div className={estilos.selos}>
          {prazos.map((prazo) => (
            <span
              key={prazo.chave}
              className={estilos.selo}
              style={{ color: `var(${prazo.token})` }}
            >
              <span aria-hidden="true">&#9679;</span>
              {prazo.rotulo}
            </span>
          ))}
        </div>
      </section>

      <section className={estilos.secao}>
        <h2 className={estilos.secaoTitulo}>Tipografia</h2>
        <p className={estilos.amostraTitulo}>Young Serif em titulo e destaque</p>
        <p className={estilos.amostraCorpo}>
          Montserrat na interface, em texto corrido, formulario e tabela.
        </p>
        <p className={estilos.amostraNumero}>
          Numeral tabular: 1234567890 &middot; 30/09/2026 &middot; 191 itens
        </p>
      </section>

      <section className={estilos.secao}>
        <h2 className={estilos.secaoTitulo}>Ações</h2>
        <div className={estilos.botoes}>
          <button type="button" className={estilos.botaoPrimario}>
            Nova acao
          </button>
          <button type="button" className={estilos.botaoSecundario}>
            Cancelar
          </button>
        </div>
      </section>
    </main>
  );
}
